import { FC, useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import CaseStepOne from "src/components/caseStepOne";
import CaseStepTwo from "src/components/caseStepTwo";
import CaseStepThree from "src/components/caseStepThree";
import RerunRestoreCaseSessionModal from "./modal";

import { AuthContext } from "src/context/authContext";
import { ToastContext } from "src/context/toastContext";
import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";
import useCaseSubmitFunc from "src/hooks/useCaseSubmitFunc";
import { populatingCloneCase, populatingPrescriptionAttributes } from "src/helpers/cloneCase";

import { ToastTypes } from "src/types";
import { UserType } from "src/types/users";
import { AddCaseProps, PatientAttributes } from "../addCase/types";
import { APIEndpoints } from "src/types/apiTypes";
import { CaseClonePathParams, CaseCloneResponseType } from "./types";
import { DEFAULT_CASE_VALUES } from "src/constants/cases";
import { CaseContext } from "src/context/caseContext";



const HubCaseClone: FC = (): JSX.Element => {
  const [step, changeStep] = useState<number>(1);
  const [isModalVisible, showHideModal] = useState<boolean>(false);
  const [isStepTwoMounted, mountingStepTwo] = useState<boolean>(false);
  const [isStepOneInvalid, setStepOneValidity] = useState<boolean>(false);
  const [isStepTwoInvalid, setStepTwoValidity] = useState<boolean>(false);

  const { userData } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const { hideLoader, setLoader } = useContext(LoaderContext);
  const { caseSchema, updateNewCaseControls } = useContext(CaseContext)
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { addCaseLoading, caseDataSubmission } = useCaseSubmitFunc()

  const formControls = useForm<AddCaseProps>({
    mode: "all",
    ...(caseSchema ?  { resolver: yupResolver(caseSchema) } : {}),
    defaultValues: {
      ...DEFAULT_CASE_VALUES,
      creator_name: userData ? userData.name : "",
      user_id: userData ? userData.id : 0,
    } as AddCaseProps,
  });
  const {
    control,
    formState,
    handleSubmit,
    getValues,
    watch,
    trigger,
    reset,
    setValue,
    resetField,
    clearErrors
  } = formControls;

  const patientData = watch("patient_attributes") as PatientAttributes;

  useEffect(() => {
    if (patientData && patientData.global_patient_id)
      trigger(["patient_attributes", "insurances_attributes"]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);

  useEffect(() => {
    if (formControls) updateNewCaseControls(formControls)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formControls])

  useEffect(() => {
    const data = localStorage.getItem('newCase');
    if (data) showHideModal(true);
  }, [])

  




  const { error: caseCloneError, data: caseCloneData, loading: caseCloneLoading } =
    useFetch<CaseCloneResponseType, {}, CaseClonePathParams>(APIEndpoints.CLONE_CASE_DETAILS, {}, { caseId: caseId ? Number(caseId) : 0 });

  useEffect(() => {
    if (caseCloneLoading === true) setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (caseCloneData || caseCloneError) {
      hideLoader();

      if (caseCloneData) {
        timeout = setTimeout(() => {
          const caseFormData = populatingCloneCase(caseCloneData, userData || {} as UserType);
          caseFormData.prescription_attributes = populatingPrescriptionAttributes(caseCloneData)
          reset(caseFormData);
           
          // console.log("case Form Data .....",caseFormData);
        }, 1500);
      }
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseCloneData, caseCloneError]);




  useEffect(() => {
    const { errors } = formState;

    if (Object.keys(errors).length > 0) {
      if (Object.hasOwn(errors, 'prescription_attributes') || Object.hasOwn(errors, 'procedure_attributes') 
        || Object.hasOwn(errors, 'shipments_attributes')
      )
        setStepOneValidity(true);
      else
        setStepOneValidity(false);

      if (Object.hasOwn(errors, 'patient_attributes') ||
        Object.hasOwn(errors, 'case_doctor') ||
        Object.hasOwn(errors, 'doctor_attributes') ||
        Object.hasOwn(errors, 'insurances_attributes')
      )
        setStepTwoValidity(true);
      else
        setStepTwoValidity(false);
    } else {
      setStepOneValidity(false);
      setStepTwoValidity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    const messages = [];
    const { errors } = formState;

    if (isStepOneInvalid && isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 1 have errors:
     ${errors.procedure_attributes ? '<br>- case procedure<br>' : ''}
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}
      ${errors.category ? '- case type' : ''}
      
      `);

      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.hasOwnProperty("phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes &&(errors.patient_attributes as PatientAttributes).address ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}
      `);
    }
    else if (isStepOneInvalid && step >= 1) {
      messages.push(`The following sections from step 1 have errors:
      ${errors.procedure_attributes ? '<br>- case procedure<br>' : ''}
      ${errors.prescription_attributes ? '- case prescription<br>' : ''}
      ${errors.category ? '- case type' : ''}
     
      `);
    }
    else if (isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.hasOwnProperty("phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && (errors.patient_attributes as PatientAttributes).address ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}`);
    }

    showToast(messages, ToastTypes.WARNING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepOneInvalid, isStepTwoInvalid, step]);


  const onStepProgressionClick = (localStep: number) => {
    if (step === 1)
      trigger(["procedure_attributes", "prescription_attributes"]);
    else if (step === 2)
      trigger(["doctor_attributes", "patient_attributes", "insurances_attributes"]);

    if (localStep >= 2)
      mountingStepTwo(true);
    else if (localStep === 3)
      trigger(["procedure_attributes", "prescription_attributes", "doctor_attributes", "patient_attributes", "insurances_attributes"]);

    changeStep(localStep);
  };


    return (
        <div className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
            <div className="bg-mist py-2">
                <div className="container-fluid">
                    <AuthSubHeader/>
                </div>
            </div>
            <div className="container-fluid pt-2 d-flex flex-column flex-fill">
                <div className="mt-2">
                    <h2 className="m-0 mb-3 page-heading">Submit Request</h2>
                </div>
                <form className="d-flex flex-column flex-fill" onSubmit={handleSubmit(caseDataSubmission)}>
                    <div className="pb-3 sticky-top top-0 z-1">
                        <div id="case-creation-progress-bar" className="case-progress-bar">
                            <button
                                type="button"
                                id="progress-step-1"
                                // className={`btn col-4 case-creation-progress-step
                                // ${caseCloneData?.data?.case?.drug_name === 'iovera°' ? 'case-creation-progress-step-blue':'' }
                                // ${step >= 1 ? isStepOneInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                                className={`btn col-4 case-creation-progress-step  
                                ${step >= 1 ? isStepOneInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                                onClick={() => onStepProgressionClick(1)}
                            >
                                1. Getting Started <i className={`bi bi-play-fill progression-arrow-head`}></i>
                            </button>

                            <button
                                type="button"
                                id="progress-step-2"
                                // className={`btn col-4 case-creation-progress-step
                                // ${caseCloneData?.data?.case?.drug_name === 'iovera°' ? 'case-creation-progress-step-blue':'' }
                                // ${step >= 2 ? isStepTwoInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                                className={`btn col-4 case-creation-progress-step   
                                ${step >= 2 ? isStepTwoInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                                onClick={() => onStepProgressionClick(2)}
                            >
                                2. Patient Information <i className={`bi bi-play-fill progression-arrow-head`}></i>
                            </button>

                            <button
                                type="button"
                                id="progress-step-3"
                                // className={`btn col-4 case-creation-progress-step
                                // ${caseCloneData?.data?.case?.drug_name === 'iovera°' ? 'case-creation-progress-step-blue':'' }
                                // ${step >= 3 ? 'active-progress-step' : ''}`}
                                className={`btn col-4 case-creation-progress-step   
                                ${step >= 3 ? 'active-progress-step' : ''}`}
                                onClick={() => onStepProgressionClick(3)}
                            >
                                3. Finish
                            </button>
                        </div>
                    </div>

                    <Controller name="original_id" control={control} defaultValue={Number(caseId)}
                                render={() => <></>}/>

                    {userData ? (
                        <>
                            <Controller
                                name="creator_name"
                                control={control}
                                defaultValue={userData.name}
                                render={() => (<></>)}
                            />

                            <Controller
                                name="user_id"
                                control={control}
                                defaultValue={userData.id}
                                render={() => (<></>)}
                            />
                        </>
                    ) : (<></>)}

                    <div id="step-1" className="case-creation-step">
                        {step >= 1 && caseCloneData &&
                            <CaseStepOne
                                control={control} watch={watch} setValue={setValue} getValues={getValues}
                                isVisible={step === 1} trigger={trigger} clearErrors={clearErrors}
                            />
                        }
                    </div>
                    <div id="step-2" className="case-creation-step">
                        {isStepTwoMounted &&
                            <CaseStepTwo resetField={resetField} control={control} getValues={getValues} watch={watch}
                                         setValue={setValue} isVisible={step === 2} trigger={trigger}
                                         clearErrors={clearErrors}
                            />
                        }
                    </div>
                    <div id="step-3" className="case-creation-step">
                        {step === 3 && <CaseStepThree getValues={getValues} control={control}/>}
                    </div>

                    <div className="d-flex mb-3 gap-2">
                        <button type="button" key="previousStepButton"
                                className={`btn btn-outline-secondary ${step === 1 ? 'd-none' : ''}`}
                                onClick={() => onStepProgressionClick(step - 1)}
                        >
                            <i className="bi bi-chevron-double-left"></i> Previous
                        </button>

                        {step === 3 ? (
                            <button type="submit" disabled={addCaseLoading} key="formSubmitButton"
                                    className="d-flex btn btn-primary"
                            >
                                {addCaseLoading ? (
                                    <i className="text-white me-2 spinner-border"
                                       style={{width: `24px`, height: `24px`}}></i>
                                ) : null}
                                Submit Request
                            </button>
                        ) : (
                            <button type="button" key="nextStepButton" className="btn btn-outline-secondary"
                                    onClick={() => onStepProgressionClick(step + 1)}
                            >
                                Next <i className="bi bi-chevron-double-right"></i>
                            </button>
                        )}
                    </div>
                </form>
            </div>
            <FooterText footerType="black"/>
            <RerunRestoreCaseSessionModal
                isModalVisible={isModalVisible}
                removeCurrentData={() => {
                    showHideModal(false)
                    localStorage.removeItem("newCase")
                }}
                restoringCaseData={() => {
                    showHideModal(false);
                    navigate("/cases/new", {state: {fromClone: true}})
                }}
            />
        </div>
    );
};

export default HubCaseClone;
